// "Katalogdan Set Oluştur" modalı (kural 11, mockup 09): müşteri + çerçeve (TODO-KPI-29:
// frameworks tablosundan, "Rapor kapsamı" sabit listesi kalktı) + set adı +
// tablo düzeni + onay kutulu katalog ağacı (varsayılan TÜMÜ seçili — çıkarma zihniyeti) + canlı
// sayaç "Oluştur (N gösterge)". Başarıda store seçimi güncellenir ve Veri Girişi'ne gidilir.
// Katalog ve Set Yönetimi boş durumundan ortak kullanılır.
// DR3 (birim varyantı): iki adım "Göstergeler → Birim tercihleri → Oluştur" — varyantlı/çevrilebilir
// gösterge seçili değilse orta adım atlanır; Geri'de seçimler korunur; tercihler fork gövdesinde
// `unitPreferences` (tek tx). Adım görünümü + saf yardımcılar ForkUnitPreferencesStep.tsx'te.
// DR11: arama alanının görünür etiketi; DR13: ≤720 px tek kolon + aile başına yerel <select>.
// Sektör paketleri (DR1/DR2/DR8/DR15): gösterge seçimi iki sekmeli `PackageSourceTabs` (Paket /
// Katalog — TEK `selected` kümesi, saf mantık lib/kpi-package-selection.ts); etkin paket varsa
// Paket sekmesinde BOŞ seçimle açılır, yoksa (liste hatası dahil) Katalog + tümü işaretli (eski
// davranış, gövde byte-eşit). Sağda özet kartı (≤720 px yapışkan alt şerit); kullanımdan kalkmış
// kalemli paket işaretliyken İleri/Oluştur pasif + neden; sunucu reddi → adım 1 + satıra odak.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  KPI_REPORT_TABLE_LAYOUTS,
  KPI_REPORT_TABLE_LAYOUT_LABELS,
  type KpiMetric,
  type KpiPackageItem,
  type KpiReportTableLayout,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import {
  forkKpiSet,
  getKpiPackage,
  kpiClientPickerItem,
  listKpiMetrics,
  searchKpiClients,
} from "../../api/kpi";
import { usePackageSelection } from "../../hooks/use-package-selection";
import type { ClientPickerItem } from "../../lib/client-picker";
import {
  forkOpening,
  forkPrimaryPlacement,
  packageUnion,
  pruneEmptiedBranches,
  summarize,
} from "../../lib/kpi-package-selection";
import {
  isPackageErrorCode,
  KPI_PACKAGES_COPY,
  packageErrorText,
} from "../../lib/kpi-packages-copy";
import { buildIndicatorTree, countSelectedLeaves } from "../../lib/kpi-set-editor";
import { defaultKpiSetName, KPI_COPY } from "../../lib/kpi-ui";
import { KPI_UNIT_COPY } from "../../lib/kpi-unit-copy";
import {
  applyFamilyChoice,
  buildUnitFamilies,
  isUnitPreferenceCandidate,
  setMetricUnit,
  type UnitFamilyRow,
} from "../../lib/kpi-unit-preferences";
import { useMediaQuery } from "../../lib/use-media-query";
import { useKpiStore } from "../../stores/kpi-store";
import { ClientPicker } from "../common/ClientPicker";
import { LoadingState } from "../common/StateSurface";
import { CATALOG_TREE_KEYS, CatalogCheckboxTree } from "./CatalogCheckboxTree";
import { ForkSummaryCard } from "./ForkSummaryCard";
import {
  ForkStepStrip,
  ForkUnitPreferencesView,
  forkSetBody,
  forkSteps,
  loadClientUnits,
  loadMetricsInUse,
  type ForkStepKey,
} from "./ForkUnitPreferencesStep";
import { FrameworkSelect } from "./FrameworkSelect";
import { KpiModal } from "./KpiModal";
import {
  errorBox,
  fieldStyle,
  footerBtn,
  helpText,
  inputStyle,
  labelStyle,
} from "./kpi-form-styles";
import { PackageSourceTabs } from "./PackageSourceTabs";

const PS = KPI_PACKAGES_COPY.summary;

export interface ForkSetModalProps {
  open: boolean;
  /** ön-seçili müşteri (kpi-store clientId ya da boş durum satırı) */
  initialClientId?: string | null;
  /** önceden yüklü katalog (Katalog sayfası) — yoksa modal kendisi çeker */
  metrics?: KpiMetric[];
  /** DR21 — Müşteri kaydı ipucundan açılış: bu paket işaretli gelir (DR2 tek istisnası) */
  initialPackageId?: string | null;
  onClose: () => void;
}

export function ForkSetModal(props: ForkSetModalProps): JSX.Element | null {
  const { open, initialClientId, metrics: preloaded, initialPackageId, onClose } = props;
  const navigate = useNavigate();
  const clients = useKpiStore((s) => s.clients);
  const storeClientId = useKpiStore((s) => s.clientId);
  const ensureClient = useKpiStore((s) => s.ensureClient);

  const year = new Date().getFullYear();
  // ClientPicker (2026-10-07): id + ad; ön-seçim store özetinden, listede yoksa tekil çağrıyla.
  const [client, setClient] = useState<ClientPickerItem | null>(null);
  const clientId = client?.id ?? "";
  // Çerçeve: null = "Çerçevesiz / Diğer". Ad elle değiştirilmediyse müşteri adından türer
  // (Finar rev-A 8 — `defaultKpiSetName`; müşteri yokken çerçeve adı + yıl).
  const [framework, setFramework] = useState<{ id: string | null; name: string | null }>({
    id: null,
    name: null,
  });
  const [name, setName] = useState(() =>
    defaultKpiSetName({ clientName: null, frameworkName: null, year }),
  );
  const [nameTouched, setNameTouched] = useState(false);
  const [layout, setLayout] = useState<KpiReportTableLayout>("both");
  const [metrics, setMetrics] = useState<KpiMetric[] | null>(preloaded ?? null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const narrow = useMediaQuery("(max-width: 720px)");
  // DR3 — adım + aile satırları (Geri'de korunur; seçim/müşteri değişince yeniden kurulur)
  const [step, setStep] = useState<ForkStepKey>("indicators");
  const [inUseMetrics, setInUseMetrics] = useState<ReadonlySet<string>>(new Set());
  const [unitRows, setUnitRows] = useState<{ key: string; rows: UnitFamilyRow[] } | null>(null);
  const [unitLoadFailed, setUnitLoadFailed] = useState(false);

  // Paket / Katalog seçimi (DR1/DR2): açılış kararı katalog + paket listesi gelince bir kez.
  const sel = usePackageSelection({
    open,
    catalog: metrics,
    clientSector: client?.sector,
    opening: (ctx, packages) =>
      forkOpening({
        leafIds: buildIndicatorTree(metrics ?? [])
          .rows.filter((r) => !r.hasChildren)
          .map((r) => r.node.id),
        packages,
        ctx,
        initialPackageId,
      }),
  });
  const selected = sel.state.selected;

  // İşaretli paketlerin kalemleri (birim varsayılanı için; liste üyelikten — ayrıntı tembel çekilir)
  const [pkgItems, setPkgItems] = useState<Record<string, KpiPackageItem[]>>({});
  const requested = useRef(new Set<string>());
  useEffect(() => {
    for (const id of sel.state.checked) {
      if (requested.current.has(id)) continue;
      requested.current.add(id);
      getKpiPackage(id)
        .then((d) => setPkgItems((prev) => ({ ...prev, [id]: d.items })))
        .catch(() => setPkgItems((prev) => ({ ...prev, [id]: [] })));
    }
  }, [sel.state.checked]);

  // Açılışta: müşteri ön-seçimi, katalog yükle (seçim kararı usePackageSelection'da).
  useEffect(() => {
    if (!open) return;
    const initial = initialClientId ?? storeClientId ?? "";
    const known = clients.find((c) => c.id === initial);
    setClient(known ? kpiClientPickerItem(known) : null);
    if (initial && !known) {
      void ensureClient(initial).then((c) => {
        if (c) setClient((prev) => prev ?? kpiClientPickerItem(c));
      });
    }
    setError(null);
    setSearch("");
    setStep("indicators");
    setUnitRows(null);
    setNameTouched(false);
    setFramework({ id: null, name: null });
    setName(defaultKpiSetName({ clientName: null, frameworkName: null, year }));
    setLayout("both");
    requested.current = new Set();
    setPkgItems({});
    let cancelled = false;
    const apply = (list: KpiMetric[]): void => {
      if (!cancelled) setMetrics(list);
    };
    if (preloaded) apply(preloaded);
    else {
      setMetrics(null);
      listKpiMetrics()
        .then((res) => apply(res.metrics))
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err.message : "Katalog yüklenemedi");
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open]);

  const tree = useMemo(() => buildIndicatorTree(metrics ?? []), [metrics]);
  const totalLeaves = tree.rows.filter((r) => !r.hasChildren).length;
  // İşaretli paketlerin birim varsayılanları (ilk paket kazanır)
  const unitDefaults = useMemo(() => {
    const out = new Map<string, string>();
    for (const id of sel.state.checked) {
      for (const it of pkgItems[id] ?? []) {
        if (it.unitDefault && !out.has(it.metricId)) out.set(it.metricId, it.unitDefault);
      }
    }
    return out;
  }, [sel.state.checked, pkgItems]);
  const summary = useMemo(
    () => summarize(sel.state, sel.ctx, { packages: sel.packages ?? [], unitDefaults }),
    [sel.state, sel.ctx, sel.packages, unitDefaults],
  );
  const union = useMemo(
    () => packageUnion(sel.state.checked, sel.ctx),
    [sel.state.checked, sel.ctx],
  );
  const count = summary.total;
  const blocked = sel.deprecatedChecked.length > 0;
  const selectedMetrics = useMemo(
    () => (metrics ?? []).filter((m) => selected.has(m.id)),
    [metrics, selected],
  );
  const steps = forkSteps(selectedMetrics);
  const hasUnitStep = steps.some((s) => s.key === "units");
  const unitKey = `${clientId}|${selectedMetrics
    .filter(isUnitPreferenceCandidate)
    .map((m) => `${m.id}:${unitDefaults.get(m.id) ?? ""}`)
    .sort()
    .join(",")}`;
  const rows = unitRows?.key === unitKey ? unitRows.rows : null;

  async function enterUnitStep(): Promise<void> {
    setStep("units");
    if (unitRows?.key === unitKey) return;
    setUnitRows(null);
    let clientUnits = new Map<string, string>();
    try {
      clientUnits = await loadClientUnits(clientId);
      setUnitLoadFailed(false);
    } catch {
      setUnitLoadFailed(true); // fail-open: sunucu mevcut müşteri kaydını zaten ezmez
    }
    const candidates = selectedMetrics.filter(isUnitPreferenceCandidate).map((m) => m.id);
    setInUseMetrics(await loadMetricsInUse(clientId, candidates));
    setUnitRows({
      key: unitKey,
      rows: buildUnitFamilies(selectedMetrics, clientUnits, unitDefaults),
    });
  }

  // Ad elle değiştirilmedikçe müşteri / çerçeve seçimini izler (müşteri ön-seçimi asenkron gelir).
  useEffect(() => {
    if (!open || nameTouched) return;
    setName(defaultKpiSetName({ clientName: client?.name, frameworkName: framework.name, year }));
  }, [open, nameTouched, client?.name, framework.name, year]);

  function changeFramework(next: { id: string | null; name: string | null }): void {
    setFramework(next);
  }

  async function submit(): Promise<void> {
    if (!clientId || !name.trim() || count === 0 || blocked) return;
    setBusy(true);
    setError(null);
    try {
      const res = await forkKpiSet(
        clientId,
        forkSetBody({
          name: name.trim(),
          frameworkId: framework.id,
          layout,
          selected,
          unitRows: hasUnitStep && step === "units" ? rows : null,
          packageIds: sel.state.checked,
        }),
      );
      const store = useKpiStore.getState();
      await store.selectClient(clientId);
      await store.selectSet(res.setId);
      onClose();
      void navigate("/clients/kpi/tablo");
    } catch (err) {
      if (err instanceof ApiError && err.code === "KPI_PACKAGE_HAS_DEPRECATED") {
        // DR8 yarış: adım 1 + Paket sekmesi + satıra odak; seçimler korunur, liste tazelenir
        setStep("indicators");
        const pid = (err.details as { packageId?: unknown } | undefined)?.packageId;
        void sel.showDeprecated(typeof pid === "string" ? pid : null);
      }
      setError(
        err instanceof ApiError
          ? isPackageErrorCode(err.code)
            ? packageErrorText(err.code, err.message, err.details)
            : err.message
          : err instanceof Error
            ? err.message
            : "Veri seti oluşturulamadı",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  const canSubmit = Boolean(clientId) && name.trim() !== "" && count > 0 && !busy && !blocked;
  const blockedProps = blocked ? { "aria-describedby": "fork-blocked-reason" } : {};
  const onUnits = hasUnitStep && step === "units";

  const placement = forkPrimaryPlacement(narrow, onUnits);
  const primary =
    hasUnitStep && !onUnits ? (
      <button
        type="button"
        style={{ ...footerBtn(true), opacity: canSubmit ? 1 : 0.5 }}
        disabled={!canSubmit}
        {...blockedProps}
        onClick={() => void enterUnitStep()}
      >
        {PS.next(count)}
      </button>
    ) : (
      <button
        type="button"
        style={{ ...footerBtn(true), opacity: canSubmit && (!onUnits || rows) ? 1 : 0.5 }}
        disabled={!canSubmit || (onUnits && !rows)}
        aria-busy={busy || undefined}
        {...blockedProps}
        onClick={() => void submit()}
      >
        {busy ? PS.creating : PS.create(count)}
      </button>
    );

  return (
    <KpiModal
      open={open}
      kicker="KATALOGDAN VERİ SETİ OLUŞTUR"
      title="Yeni veri seti"
      description="Seçtiğiniz göstergeler kataloğun kopyası olarak müşteriye özel veri setine alınır; sonradan serbestçe düzenleyebilirsiniz."
      onClose={onClose}
      width={680}
      // güvenlik/UX taraması 2026-10-08 (U-M2): gösterge seçimi ya da elle ad varken kapatma onayı
      dirty={!busy && (selected.size > 0 || sel.state.checked.length > 0 || nameTouched)}
      footer={
        <>
          {blocked ? (
            <span
              id="fork-blocked-reason"
              className="mr-auto"
              style={{ ...helpText, color: "var(--color-warning-text)" }}
            >
              {KPI_PACKAGES_COPY.deprecated.blockedReason}
            </span>
          ) : (
            <span className="text-xs mr-auto" style={{ color: "var(--color-text-3)" }}>
              Katalogla bağ: gösterge kodları korunur; katalog değişikliği mevcut veri setini
              etkilemez.
            </span>
          )}
          <button type="button" style={footerBtn()} onClick={onClose} disabled={busy}>
            İptal
          </button>
          {onUnits ? (
            <button
              type="button"
              style={footerBtn()}
              disabled={busy}
              onClick={() => setStep("indicators")}
            >
              {KPI_UNIT_COPY.fork.back}
            </button>
          ) : null}
          {placement === "footer" ? primary : null}
        </>
      }
    >
      {hasUnitStep ? (
        <ForkStepStrip steps={steps} current={onUnits ? "units" : "indicators"} />
      ) : null}
      {onUnits ? (
        <ForkUnitPreferencesView
          rows={rows}
          narrow={narrow}
          loadFailed={unitLoadFailed}
          disabled={busy}
          inUse={inUseMetrics}
          onChoose={(key, unit) =>
            setUnitRows((u) => u && { key: u.key, rows: applyFamilyChoice(u.rows, key, unit) })
          }
          onMetricUnit={(key, metricId, unit) =>
            setUnitRows(
              (u) => u && { key: u.key, rows: setMetricUnit(u.rows, key, metricId, unit) },
            )
          }
        />
      ) : null}
      <div style={onUnits ? { display: "none" } : undefined}>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "1fr 1fr" }}
        >
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="fork-client">
              Müşteri
            </label>
            <ClientPicker
              id="fork-client"
              ariaLabel="Müşteri"
              placeholder="Seçin…"
              value={client}
              disabled={busy}
              onChange={setClient}
              fetchOptions={searchKpiClients}
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="fork-framework">
              {KPI_COPY.framework.label}
            </label>
            <FrameworkSelect
              id="fork-framework"
              value={framework.id}
              disabled={busy}
              onChange={changeFramework}
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="fork-name">
              {KPI_COPY.setNameLabel}
            </label>
            <input
              id="fork-name"
              style={inputStyle}
              value={name}
              disabled={busy}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="fork-layout">
              Tablo düzeni
            </label>
            <select
              id="fork-layout"
              style={inputStyle}
              value={layout}
              disabled={busy}
              onChange={(e) => setLayout(e.target.value as KpiReportTableLayout)}
            >
              {KPI_REPORT_TABLE_LAYOUTS.map((l) => (
                <option key={l} value={l}>
                  {KPI_REPORT_TABLE_LAYOUT_LABELS[l]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={fieldStyle}>
          <span style={labelStyle}>
            Göstergeler
            {sel.ready && !sel.hasActivePackages ? (
              <span style={{ textTransform: "none", fontWeight: 400 }}>
                {" "}
                — varsayılan: tümü seçili, ihtiyaç dışı olanları kaldırın (
                {countSelectedLeaves(tree, selected)} / {totalLeaves})
              </span>
            ) : null}
          </span>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) 200px" }}
          >
            <PackageSourceTabs
              idPrefix="fork-src"
              mode="fork"
              tab={sel.tab}
              onTabChange={sel.setTab}
              packages={sel.packages}
              loadFailed={sel.loadFailed}
              suggested={sel.suggested}
              showNoSuggestion={sel.showNoSuggestion}
              checked={sel.state.checked}
              onToggle={sel.toggle}
              onRecheck={() => void sel.recheck()}
              disabled={busy}
              catalogCounts={
                sel.hasActivePackages ? { added: summary.added, removed: summary.removed } : null
              }
              focusRequest={sel.focusRequest}
              ready={sel.ready}
              catalogPanel={
                <div className="grid gap-1">
                  <label style={labelStyle} htmlFor="fork-search">
                    {KPI_UNIT_COPY.fork.searchLabel}
                  </label>
                  <input
                    id="fork-search"
                    type="search"
                    placeholder="Kategori veya gösterge ara…"
                    style={inputStyle}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {/* tek kaydırıcı: ağaç kendi kabında kayar (DR15 — en çok 320 px) */}
                  <div className="rounded-lg" style={{ border: "1px solid var(--color-border)" }}>
                    {metrics === null ? (
                      <LoadingState label="Katalog yükleniyor…" />
                    ) : (
                      <CatalogCheckboxTree
                        collapseKey={CATALOG_TREE_KEYS.forkSet}
                        maxHeight={320}
                        alwaysScroll
                        metrics={metrics}
                        selected={selected}
                        search={search}
                        packageMembers={union}
                        onChange={(next) =>
                          sel.catalogChange(pruneEmptiedBranches(selected, next, sel.ctx))
                        }
                      />
                    )}
                  </div>
                </div>
              }
            />
            <ForkSummaryCard
              summary={summary}
              withPackages={sel.state.checked.length > 0}
              narrow={narrow}
              {...(placement === "strip" ? { action: primary } : {})}
            />
          </div>
        </div>
      </div>
      {error ? (
        <div style={errorBox} role="alert">
          {error}
        </div>
      ) : null}
    </KpiModal>
  );
}
